import {
  Box,
  Flex,
  IconButton,
  LinkOverlay,
  ListItem,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { toPlainText } from '@portabletext/react';
import { Drawer } from '@time-line/components/common';
import { PersonBioDrawer } from '@time-line/components/drawers';
import { Plus } from '@time-line/components/icons';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import type { Person as PersonType } from 'types/cms/Person';

interface PersonProps extends PersonType {
  drawerHeader: {
    title?: string;
    subTitle?: string;
  };
}

export function Person({
  role,
  name,
  image,
  biography,
  drawerHeader,
}: PersonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const imageProps = useImage(image || null, { ratio: 345 / 430 });

  return (
    <>
      <ListItem
        className={`person-${name}`}
        gridColumn={{ base: 'span 6', md: 'span 4', xl: 'span 2' }}
        position="relative"
        display="flex"
        height="100%"
        flexDir="column"
        gap="2xs"
      >
        {imageProps && (
          <Box borderRadius="base" overflow="hidden">
            <Image
              {...imageProps}
              sizes="(min-width: 768px) 33vw, (min-width: 1920px) 345px, 100vw"
            />
          </Box>
        )}
        <Stack spacing="2xs">
          <Flex alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Text as="h2" size="title3">
                {name}
              </Text>
              <Text size="body3" color="blackAlpha.700">
                {role}
              </Text>
            </Box>
            <LinkOverlay
              onClick={onOpen}
              flexShrink={0}
              as={IconButton}
              position="static"
              variant="unstyled"
              aria-label={`Read more about ${name}`}
              icon={<Plus boxSize="sm" />}
            />
          </Flex>
          {biography && (
            <Text
              color="blackAlpha.700"
              size="detail"
              whiteSpace="pre-wrap"
              noOfLines={{ base: 4, sm: 2 }}
            >
              {toPlainText(biography)}
            </Text>
          )}
        </Stack>
      </ListItem>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={drawerHeader.title}
        subtitle={drawerHeader.subTitle}
        maxWidth="lg"
      >
        <PersonBioDrawer
          name={name}
          role={role}
          image={image}
          biography={biography}
        />
      </Drawer>
    </>
  );
}
